<template>
  <!-- Entire row links to the group page; whole card renders only once a group is passed in -->
  <RouterLink v-if="group" class="group-item" :to="`/group/${group.id}`">
    <div class="group-item__layout flex items-center justify-between gap-3">

      <!-- Left: Avatar + Group Info -->
      <div class="group-item__left-section flex items-center gap-3 flex-1 min-w-0">

        <!-- Group Avatar -->
        <div class="group-item__avatar-cell flex-shrink-0">
          <BaseAvatarNew
            :storage-id="group.avatarFile?.storageId"
            :href="`/group/${group.id}`"
            :isOrg="true"
          />
        </div>

        <!-- Name + username + member count -->
        <div class="group-item__info-cell flex flex-col min-w-0">
          <div class="group-item__name group-item__author-name hover:underline overflow-hidden text-ellipsis whitespace-nowrap cursor-pointer">
            {{ trimGroupName(group.name) }}
          </div>
          <div v-if="group.username" class="group-item__username text-[0.75rem] text-[var(--color-text-02)] hover:underline cursor-pointer">
            @{{ group.username }}
          </div>
          <div v-if="group.membersCount !== undefined" class="group-item__meta text-[0.72rem] text-[var(--color-text-02)] mt-0.5">
            {{ group.membersCount ?? 0 }} {{ group.membersCount === 1 ? 'member' : 'members' }}
          </div>
        </div>
      </div>

      <!-- Right: Join button -->
      <!-- Follow/UnFollow commented out — replaced with Join / Request to Join -->
      <!-- <div v-if="!group.isPrivate && isFollowChannel" class="group-item__right-section flex-shrink-0" @click.prevent="$emit('follow', group)">
        <button class="group-item__follow-btn group-item__follow-btn--following" type="button" @click="handleUnFollowChannel">UnFollow</button>
      </div>
      <div v-if="!group.isPrivate && !isFollowChannel" class="group-item__right-section flex-shrink-0" @click.prevent="$emit('follow', group)">
        <button class="group-item__follow-btn group-item__follow-btn--following" type="button" @click="handleFollowChannel('follow')">Follow</button>
      </div>
      <div v-if="group.isPrivate" class="group-item__right-section flex-shrink-0" @click.prevent="$emit('follow', group)">
        <button class="group-item__follow-btn group-item__follow-btn--following" type="button" @click="handleRequestToJoinGroup">Join</button>
      </div> -->

      <!-- Right: Join button — one of three states depending on privacy/membership/request status -->
      <div v-if="!group.isPrivate && !isAlreadyGroupMember" class="group-item__right-section flex-shrink-0" @click.prevent>
        <button
          class="group-item__follow-btn"
          type="button"
          :disabled="isGroupJoining"
          @click="handleJoinGroup"
        >
          {{ isGroupJoining ? 'Joining...' : 'Join' }}
        </button>
      </div>
      <div v-if="group.isPrivate && !isGroupAlreadyRequested" class="group-item__right-section flex-shrink-0" @click.prevent>
        <button
          class="group-item__follow-btn group-item__follow-btn--request"
          type="button"
          @click="handleRequestToJoinGroup"
        >
          Request to Join
        </button>
      </div>
      <div v-if="group.isPrivate && isGroupAlreadyRequested" class="group-item__right-section flex-shrink-0" style="border: 1px solid var(--color-text-02);border-radius: 8px;padding: 4px 10px;">
        <span class="group-item__join-requested-label">Requested</span>
      </div>

    </div>
  </RouterLink>
</template>

<script setup>
/**
 * SuggestedGroupItem — compact row used in "suggested groups" lists. Shows
 * avatar, trimmed name, username and member count, plus a right-hand action
 * that is one of: Join (public, not a member), Request to Join (private,
 * not yet requested), or a static "Requested" label (private, already
 * requested).
 */
import { ref, computed } from 'vue';
import { useStore } from 'vuex';
import { useToast } from 'primevue/usetoast';
import BaseAvatarNew from '@/components/ui-components/NewBaseAvatarNew/index.vue';

const store = useStore();
const toast = useToast();

const props = defineProps({
  group: {
    type: Object,
    default: null,
  },
  showFollowButton: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['follow', 'follow-channel', 'unfollow-channel']);

// Check actual membership from store (persists across renders/sessions)
/** Whether the current user is already a member of this group, via the store or a just-completed join. */
const isAlreadyGroupMember = computed(() => {
  if (hasJoinedGroup.value) return true;
  const myOrgs = store.getters.getOnlyUserOrgs || [];
  return myOrgs.some(org => org.id === props.group?.id);
});

// Public group: join directly (same flow as org.vue handleJoinGroup)
const isGroupJoining = ref(false);
const hasJoinedGroup = ref(false);
/** Joins a public group directly, updates the user's orgs in the store, and shows a success toast. */
const handleJoinGroup = async () => {
  if (isGroupJoining.value || hasJoinedGroup.value) return;
  isGroupJoining.value = true;
  try {
    const res = await store.dispatch('fetchRequestToJoin', { id: props.group.id, skipToast: true });
    if (res?.ok) {
      hasJoinedGroup.value = true;
      store.commit('addMyOrg', props.group);
      store.dispatch('fetchOtherOrgs');
      store.dispatch('fetchOnlyUserOrgs');
      toast.add({ severity: 'success', summary: 'Joined!', detail: `You have joined ${props.group.name}.`, life: 4000 });
    }
  } finally {
    isGroupJoining.value = false;
  }
};

// Private group: check if request already sent (from group data or local state)
const currentUser = computed(() => store.getters.getUser);
const groupJoinRequested = ref(false);
/** Whether the current user has already requested to join this private group. */
const isGroupAlreadyRequested = computed(() => {
  if (groupJoinRequested.value) return true;
  const requests = props.group?.joinRequests || [];
  return requests.some(id => id === currentUser.value?.id);
});

/** Sends a join request for a private group, refreshes suggested groups, and shows a confirmation toast. */
const handleRequestToJoinGroup = async () => {
  if (isGroupAlreadyRequested.value) return;
  await store.dispatch('fetchRequestToJoin', { id: props.group.id, skipToast: true });
  groupJoinRequested.value = true;
  store.dispatch('fetchOtherOrgs');
  toast.add({ severity: 'info', summary: 'Request Sent', detail: `Your request to join ${props.group.name} has been submitted.`, life: 4000 });
};

// Commented out — Follow/UnFollow replaced by Join / Request to Join
// const handleFollowChannel = async (type) => { ... };
// const handleUnFollowChannel = async () => { ... };
// const isFollowChannel = computed(() => { ... });

/** Truncates a group name to `max` characters, appending ".." when it's cut short. */
const trimGroupName = (text, max = 20) => {
  if (!text) return '';
  return text.length <= max ? text : text.slice(0, max) + '..';
};
</script>

<style scoped lang="scss">
.group-item {
  padding: 10px 8px;
  display: block;
  transition: 0.2s ease-in;
  border-bottom: 1px solid var(--color-box-border);
  border-radius: 0;
  text-decoration: none;

  &:hover {
    background-color: var(--color-theme-wise-hover, rgba(0, 0, 0, 0.02));
  }
}

.group-item__layout {
  width: 100%;
}

.group-item__avatar-cell {
  display: flex;
  justify-content: center;
  align-items: center;
}

.group-item__info-cell {
  flex: 1;
  min-width: 0;
}

.group-item__name {
  font-size: 0.85rem;
  font-weight: 500;
  color: var(--color-foreground-primary);
  text-decoration: none;
}

.group-item__username {
  font-size: 0.75rem;
  color: var(--color-text-02);
  text-decoration: none;
}

.group-item__meta {
  font-size: 0.72rem;
  color: var(--color-text-02);
}

.group-item__right-section {
  display: flex;
  align-items: center;
}

.group-item__follow-btn {
  padding: 5px 10px;
  border-radius: 6px;
  font-size: 0.72rem;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.15s, color 0.15s;
  border: 1.5px solid var(--color-quick-link-icon-bg);
  background: var(--color-quick-link-icon-bg);
  color: var(--color-text-black-white);
  white-space: nowrap;

  &:disabled {
    opacity: 0.6;
    cursor: no-drop;
  }

  &--request {
    min-width: 100px;
  }
}

.group-item__join-requested-label {
  font-size: 0.72rem;
  font-weight: 600;
  color: var(--color-text-02);
  white-space: nowrap;
}
</style>
